import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../css/chatbot.css';
import ReactMarkdown from 'react-markdown';

const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [thread, setThread] = useState(null);
    const [thinkingDots, setThinkingDots] = useState('...');
    const [awaitingClarification, setAwaitingClarification] = useState(false);
    const messagesEndRef = useRef(null);
    const userMessageCountRef = useRef(0);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        const isSecondOrLaterUserMessage =
            lastMessage?.sender === 'user' && userMessageCountRef.current > 1;
        const isBotClarification = lastMessage?.isClarificationPrompt;

        if (isSecondOrLaterUserMessage || isBotClarification) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        let interval;
        if (loading) {
            interval = setInterval(() => {
                setThinkingDots(prev =>
                    prev === '.' ? '..' : prev === '..' ? '...' : '.'
                );
            }, 500);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const sendMessage = async () => {
        if (!input.trim() || loading) return;

        const newMessages = [...messages, { sender: 'user', text: input }];
        setMessages(newMessages);
        setInput('');
        setLoading(true);
        userMessageCountRef.current += 1;

        const isClarification = awaitingClarification;
        if (awaitingClarification) {
            setAwaitingClarification(false);
        }

        const headers = {
            Authorization: `Bearer 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`,
            'Content-Type': 'application/json',
            'OpenAI-Beta': 'assistants=v2',
        };

        try {
            let threadId = thread;

            if (!threadId) {
                const threadRes = await axios.post(
                    'https://api.openai.com/v1/threads',
                    { messages: [{ role: 'user', content: input }] },
                    { headers }
                );
                threadId = threadRes.data.id;
                setThread(threadId);
            } else {
                await axios.post(
                    `https://api.openai.com/v1/threads/${threadId}/messages`,
                    { role: 'user', content: input },
                    { headers }
                );
            }

            const runRes = await axios.post(
                `https://api.openai.com/v1/threads/${threadId}/runs`,
                { assistant_id: 'asst_NwLtHFpAM68zbQakAWLnpmwa' },
                { headers }
            );
            const runId = runRes.data.id;

            let runStatus = 'queued';
            while (runStatus !== 'completed') {
                const statusRes = await axios.get(
                    `https://api.openai.com/v1/threads/${threadId}/runs/${runId}`,
                    { headers }
                );
                runStatus = statusRes.data.status;
                await delay(1000);
            }

            const msgRes = await axios.get(
                `https://api.openai.com/v1/threads/${threadId}/messages`,
                { headers }
            );

            const assistantText = msgRes.data.data[0].content[0].text.value;

            const newBotMessage = {
                sender: 'bot',
                text: assistantText,
                messageId: msgRes.data.data[0].id,
                feedbackGiven: false,
                isFollowUpToClarification: isClarification
            };

            setMessages([...newMessages, newBotMessage]);
        } catch (error) {
            console.error('Error fetching chatbot response:', error);
            setMessages([
                ...newMessages,
                { sender: 'bot', text: 'Sorry, something went wrong.' },
            ]);
        }

        setLoading(false);
    };

    const sendFeedback = (messageId, feedback) => {
        setMessages((prevMessages) => {
            const updatedMessages = prevMessages.map((msg) =>
                msg.messageId === messageId
                    ? { ...msg, feedbackGiven: true }
                    : msg
            );

            if (feedback === 'not_helpful') {
                const apologyMessage = {
                    sender: 'bot',
                    text: "I'm sorry that wasn't helpful. Could you tell me what was confusing or how I can clarify?",
                    isClarificationPrompt: true
                };
                setAwaitingClarification(true);
                return [...updatedMessages, apologyMessage];
            }

            return updatedMessages;
        });
    };

    return (
        <div className="chat-container">
            <h3 className="title">Chat with Assistant</h3>
            <div className="message-container">
                {messages.map((msg, index) => (
                    <div
                        key={index}
                        className={`${msg.sender === 'user' ? 'user-message' : 'bot-message'} ${msg.isClarificationPrompt ? 'clarification-message' : ''}`}
                    >
                        <span className="message-bubble">
                            <ReactMarkdown>{msg.text.replace(/【[^】]*】/g, '')}</ReactMarkdown>
                        </span>

                        {msg.sender === 'bot' && !msg.feedbackGiven && !msg.isClarificationPrompt && (
                            <div className="feedback-box">
                                <div className="feedback-buttons">
                                    <button onClick={() => sendFeedback(msg.messageId, 'helpful')}>👍 Helpful</button>
                                    <button onClick={() => sendFeedback(msg.messageId, 'not_helpful')}>👎 Not Helpful</button>
                                </div>
                            </div>
                        )}

                        {msg.sender === 'bot' && msg.feedbackGiven && !msg.isClarificationPrompt && (
                            <div className="feedback-confirmed">✅ Feedback received</div>
                        )}
                    </div>
                ))}
                {loading && (
                    <div className="bot-message">
                        <div className="message-bubble">
                            Thinking{thinkingDots}
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="input-container">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading) {
                            sendMessage();
                        }
                    }}
                    placeholder="Ask me something..."
                    className="input"
                    disabled={loading}
                />
                <button onClick={sendMessage} className="button" disabled={loading}>
                    {loading ? '...' : 'Send'}
                </button>
            </div>
        </div>
    );
};

export default Chatbot;
